import React from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { MapPin, CalendarDays, Phone, Loader2, AlertTriangle, Info, Clock, Navigation } from "lucide-react";
import { FASCE_VISITA_LABEL } from "../../lib/constants";
import { buildGoogleMapsUrl } from "./printSheet";
import { toast } from "sonner";

// Limite pratico Google Maps Directions (origine + 9 waypoint + destinazione = ~11)
const GMAPS_WAYPOINT_LIMIT = 9;

const GIORNO_LABEL = {
  LUN: "Lunedì", MAR: "Martedì", MER: "Mercoledì",
  GIO: "Giovedì", VEN: "Venerdì",
};

/**
 * Sezione di sola lettura per la modalità "Preview da zone" (BLOCCO B4).
 *
 * Consuma /api/giro/preview-zone (vedi services/giri_zone.py).
 * NON permette: registrazione ritiro, cambio stato, assegnazione mezzo/trasportatore.
 * Mostra solo l'elenco zone+fermate dovute secondo `zone.giornoServizio`.
 */
/**
 * Click diretto utente → apre Google Maps (no popup blocking).
 * Gestisce: niente fermate / niente coordinate / >9 waypoints.
 */
function openMapsForZona(deposito, fermate) {
  if (!fermate || fermate.length === 0) {
    toast.error("Nessuna fermata in questa zona.");
    return;
  }
  let target = fermate;
  let avvisoLimite = null;
  if (target.length > GMAPS_WAYPOINT_LIMIT) {
    target = target.slice(0, GMAPS_WAYPOINT_LIMIT);
    avvisoLimite = `Google Maps supporta max ${GMAPS_WAYPOINT_LIMIT} fermate per percorso. Apro le prime ${GMAPS_WAYPOINT_LIMIT} di ${fermate.length}.`;
  }
  const { url, included, excluded, depositoExcluded } = buildGoogleMapsUrl(deposito, target);
  if (!url) {
    toast.error("Nessuna fermata ha coordinate o indirizzo valido. Impossibile aprire Google Maps.");
    return;
  }
  if (avvisoLimite) toast.warning(avvisoLimite);
  window.open(url, "_blank", "noopener,noreferrer");
  if (excluded.length > 0) {
    const names = excluded.slice(0, 3).map(({ stop, motivo }) =>
      `${stop.ragioneSociale || stop.id?.slice(0,8) || "?"} (${stop.indirizzo || "?"}) — ${motivo}`
    ).join("\n");
    const more = excluded.length > 3 ? `\n…e altre ${excluded.length - 3}` : "";
    toast.warning(
      `Google Maps aperto con ${included.length} fermate. ${excluded.length} esclusa/e perché l'indirizzo è da verificare.\n\n${names}${more}`,
      { duration: 10000 }
    );
  }
  if (depositoExcluded) {
    toast.warning(
      `Deposito non incluso nel percorso: ${depositoExcluded}. Configurare l'indirizzo del deposito per migliorare il giro.`,
      { duration: 10000 }
    );
  }
}


export function PreviewZoneSection({ previewData, loading, isCarrier }) {
  if (loading) {
    return (
      <Card className="p-8 text-center text-slate-500" data-testid="preview-zone-loading">
        <Loader2 size={28} className="mx-auto animate-spin text-[#1565C0] mb-2" />
        Caricamento preview…
      </Card>
    );
  }
  if (!previewData) return null;

  const { giorno, settimana, data, zone = [], totale_produttori = 0, messaggio } = previewData;

  return (
    <div className="space-y-3" data-testid="preview-zone-section">
      <Card className="p-3 bg-[#1565C0]/10 border border-[#1565C0]/30">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-[#1565C0] shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700">
            <span className="font-semibold text-[#1565C0]">Preview da zone (B3/B4)</span> · sola consultazione.
            Mostra cosa <em>dovrebbe</em> essere visitato in base a <code>zone.giornoServizio</code>,
            senza assegnazione mezzo/trasportatore e senza persistenza. La modalità operativa
            resta &quot;Giro legacy&quot;.
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border border-slate-200 shadow-sm" data-testid="preview-zone-header">
        <div className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              {data} · {GIORNO_LABEL[giorno] || giorno} · {settimana}
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {zone.length} {zone.length === 1 ? "zona" : "zone"} attive ·
              <span className="text-[#1B5E20] ml-1">{totale_produttori}</span> produttori dovuti
            </div>
          </div>
        </div>
      </Card>

      {messaggio && (
        <Card className="p-4 bg-[#F57C00]/10 border border-[#F57C00]/40" data-testid="preview-zone-empty">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-[#F57C00] shrink-0 mt-0.5" />
            <div className="text-sm text-slate-800">{messaggio}</div>
          </div>
        </Card>
      )}

      {zone.map((z) => (
        <Card key={z.zonaId} className="p-4 bg-white border border-slate-200 shadow-sm" data-testid={`preview-zona-${z.zonaId}`}>
          <div className="flex items-start gap-3 flex-wrap mb-2">
            <div
              className="h-10 w-10 rounded-xl flex items-center justify-center text-white shrink-0"
              style={{ backgroundColor: z.colore || "#1B5E20" }}
            >
              <MapPin size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{z.nome}</div>
                {z.giornoServizio && (
                  <Badge className="bg-[#1565C0] text-white text-[10px] flex items-center gap-1">
                    <CalendarDays size={10} /> {GIORNO_LABEL[z.giornoServizio]}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px]">
                  {z.totale_produttori} produttori
                </Badge>
                {typeof z.totalKm === "number" && z.totalKm > 0 && (
                  <Badge variant="outline" className="text-[10px] text-slate-500">
                    {Math.round(z.totalKm)} km TSP
                  </Badge>
                )}
              </div>
            </div>
            {z.fermate && z.fermate.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs shrink-0"
                onClick={() => openMapsForZona(previewData.deposito, z.fermate)}
                data-testid={`preview-gmaps-${z.zonaId}`}
              >
                <Navigation size={12} className="mr-1" /> Apri in Google Maps
              </Button>
            )}
          </div>

          {z.fermate && z.fermate.length > 0 ? (
            <ul className="space-y-1.5 mt-2" data-testid={`preview-fermate-${z.zonaId}`}>
              {z.fermate.map((s, idx) => (
                <li
                  key={s.id}
                  className="flex items-start gap-2 bg-slate-50 rounded-lg p-2 border border-slate-200"
                  data-testid={`preview-fermata-${s.id}`}
                >
                  <div className="h-7 w-7 rounded-md bg-[#1565C0]/10 text-[#1565C0] flex items-center justify-center text-xs font-bold shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">
                      {s.ragioneSociale}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {s.indirizzo}{s.citta ? `, ${s.citta}` : ""}{s.cap ? ` ${s.cap}` : ""}
                    </div>
                    {(s.telefonoMobile || s.telefonoFisso) && !isCarrier && (
                      <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                        <Phone size={10} /> {s.telefonoMobile || s.telefonoFisso}
                      </div>
                    )}
                  </div>
                  {s.fasciaOrariaVisita && (
                    <Badge
                      variant="outline"
                      className="text-[10px] shrink-0 flex items-center gap-0.5"
                      data-testid={`fascia-visita-${s.id}`}
                    >
                      <Clock size={9} />
                      {FASCE_VISITA_LABEL[s.fasciaOrariaVisita] || s.fasciaOrariaVisita}
                    </Badge>
                  )}
                  {s.fasciaOraria && !s.fasciaOrariaVisita && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {s.fasciaOraria}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-slate-400 italic mt-2">
              Nessun produttore dovuto in questa zona per la data selezionata.
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

/*
 * ConfrontoLegacyPreview è stato rimosso insieme al modello Legacy (regola zone-only).
 * Il file resta senza export legacy: solo `PreviewZoneSection` è ancora esposto.
 */
